import React, {PropsWithChildren, useEffect, useMemo, useRef, useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import type {ReportsPage_viewer$data as ReportsPage_viewer} from './__generated__/ReportsPage_viewer.graphql';
import {RouteComponentProps} from 'react-router-dom';
import Styled from 'styled-components';
import {Button, FlexColumn, FlexRow, Heading, SearchField, Tabs} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import {ReportRow} from './ReportTypes';
import {selectPrivateReports, selectSharedReports} from './ReportTabSelectors';
import {getNotNullishNodes} from '../../shared/util/NotNullPredicate';
import Util from '../../shared/util/util';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import ReportsPageChangeViewButton from './ReportsPageChangeViewButton';
import {mapReportToRow} from './ReportRowMapper';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import ReportsTabContent from './ReportsTabContent';
import {hasPermission} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {createReport} from './actions/create-report/CreateReport';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {useLocalStorage} from 'react-use';

export type UseHistoryType = RouteComponentProps['history'];

const GROUP_BY_LOCALSTORAGE_KEY = 'report-page-group-by';

const Toolbar = ({children}: PropsWithChildren<unknown>) => <FlexRow>{children}</FlexRow>;
Toolbar.Left = ({children}: PropsWithChildren<unknown>) => <FlexRow gap={'s'}>{children}</FlexRow>;
Toolbar.Right = ({children}: PropsWithChildren<unknown>) => (
	<FlexRow gap={'s'} justifyContent={'end'}>
		{children}
	</FlexRow>
);

const PageWrapper = Styled.div`
  padding: 32px;
`;

interface ReportsPageProps {
	viewer: ReportsPage_viewer;
}

const ReportsPageTabs = {
	private: 'private',
	shared: 'shared',
	all: 'all',
};

const ReportsPage = ({viewer}: ReportsPageProps): React.JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const scrollRef = useRef(null);
	const [search, setSearch] = useState('');

	const [tab, setTab] = useLocalStorage<string>('ReportsPage.SelectedTab', ReportsPageTabs.shared);
	const selectedTab = tab ?? ReportsPageTabs.shared;

	const [groupings, setGroupings] = useState<string[]>(() =>
		JSON.parse(Util.localStorageGetItem(GROUP_BY_LOCALSTORAGE_KEY) || JSON.stringify([]))
	);

	useEffect(() => {
		document.title = `Reports - Forecast`;
	}, []);

	useTrackPage('Reports', {tabName: selectedTab});

	const groupingColumnHeader = groupings.length
		? formatMessage({id: 'report.grouping'}) +
		  ': ' +
		  groupings.map(grouping => formatMessage({id: 'report.column.' + grouping})).join(' / ')
		: 'Name';

	const onGroupingsChange = (groupings: string[]) => {
		Util.localStorageSetItem(GROUP_BY_LOCALSTORAGE_KEY, JSON.stringify(groupings));
		setGroupings(groupings);

		const groupingObj = groupings.reduce((result, item, index) => {
			result[`level_${index + 1}`] = item;
			return result;
		}, {});

		trackEvent('Standard Report', 'Grouping Changed', {
			groupBy: groupings,
			...groupingObj,
		});
	};

	// Release sequencing only, so the type names land with the report builder rather than ahead of it.
	// They are meant to reach everyone once it is live.
	//
	// Both of canOpenReportBuilder()'s company flags, not just the first: a company holding
	// ai_report_builder without forecast_agent_chat cannot open the builder at all, so suffixing its
	// rows "(Standard)" would name a distinction it can never see the other half of. The two
	// per-viewer halves of that gate are deliberately left out - INSIGHTS_READ and client-user status
	// vary between people, and folding them in would show two viewers different names for one row.
	//
	// The AI marker is deliberately not gated: a report built while the builder was on stays
	// identifiable afterwards.
	const builderEnabled = hasFeatureFlag('ai_report_builder') && hasFeatureFlag('forecast_agent_chat');

	const allReports: ReportRow[] = useMemo(() => {
		return getNotNullishNodes(viewer?.company?.allSavedReports?.edges)
			.filter(report => !report.converted)
			.map(report => mapReportToRow(report, viewer.actualPersonId, formatMessage, builderEnabled));
	}, [viewer?.company?.allSavedReports, viewer.actualPersonId, formatMessage, builderEnabled]);

	const privateReports: ReportRow[] = useMemo(() => selectPrivateReports(allReports), [allReports]);
	const sharedReports: ReportRow[] = useMemo(() => selectSharedReports(allReports), [allReports]);

	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);

	return (
		<CustomScrollDiv
			setScrollbarsRef={node => {
				scrollRef.current = node;
			}}
		>
			<PageWrapper>
				<FlexColumn gap={'l'}>
					<Toolbar>
						<Toolbar.Left>
							<Heading size={'3'}>
								<FormattedMessage id="header.reports" />
							</Heading>
						</Toolbar.Left>
						<Toolbar.Right>
							<SearchField value={search} onChange={setSearch} data-cy={'reports-search'} />
							<ReportsPageChangeViewButton groupings={groupings} onGroupingsChange={onGroupingsChange} />
							{/* The label is passed as a string, not as a FormattedMessage element. The design
							    system button derives its accessible name from its children, so an element child
							    stringifies and a screen reader announces "[object Object]" while sighted users
							    read the correct text. */}
							<Button data-cy={'create-reports-button'} onPress={createReport}>
								{formatMessage({id: 'reports.create-report'})}
							</Button>
						</Toolbar.Right>
					</Toolbar>
					<Tabs value={selectedTab} onValueChange={setTab}>
						<Tabs.List>
							<Tabs.Tab
								data-cy={'my-reports-tab'}
								value={ReportsPageTabs.private}
							>{`Private (${privateReports.length})`}</Tabs.Tab>
							<Tabs.Tab
								data-cy={'shared-reports-tab'}
								value={ReportsPageTabs.shared}
							>{`Shared (${sharedReports.length})`}</Tabs.Tab>
							{isAdmin && (
								<Tabs.Tab
									data-cy={'all-reports-tab'}
									value={ReportsPageTabs.all}
								>{`All (${allReports.length})`}</Tabs.Tab>
							)}
						</Tabs.List>
						<Tabs.Content value={ReportsPageTabs.private}>
							<ReportsTabContent
								enableSelection={true}
								company={viewer.company}
								reports={privateReports}
								search={search}
								groupings={groupings}
								scrollRef={scrollRef}
								groupingColumnHeader={groupingColumnHeader}
							/>
						</Tabs.Content>
						<Tabs.Content value={ReportsPageTabs.shared}>
							<ReportsTabContent
								enableSelection={isAdmin}
								company={viewer.company}
								reports={sharedReports}
								search={search}
								groupings={groupings}
								scrollRef={scrollRef}
								groupingColumnHeader={groupingColumnHeader}
							/>
						</Tabs.Content>
						{isAdmin && (
							<Tabs.Content value={ReportsPageTabs.all}>
								<ReportsTabContent
									enableSelection={true}
									company={viewer.company}
									reports={allReports}
									search={search}
									groupings={groupings}
									scrollRef={scrollRef}
									groupingColumnHeader={groupingColumnHeader}
								/>
							</Tabs.Content>
						)}
					</Tabs>
				</FlexColumn>
			</PageWrapper>
		</CustomScrollDiv>
	);
};

export default createFragmentContainer(ReportsPage, {
	viewer: graphql`
		fragment ReportsPage_viewer on Viewer {
			id
			actualPersonId
			availableFeatureFlags {
				key
			}
			company {
				...ReportsTable_company
				id
				modules {
					moduleType
				}
				allSavedReports(first: 100000, state: "SAVED") @connection(key: "Company_allSavedReports", filters: []) {
					edges {
						node {
							id
							name
							type
							person {
								id
								fullName
								email
								profilePictureId
							}
							lastViewed
							lastViewedName
							lastViewedBy {
								id
								fullName
								email
								profilePictureId
							}
							converted
							reportService
							sharedWithViewer
							source
							shares(first: 100) @connection(key: "Report_shares", filters: []) {
								edges {
									node {
										id
										permissionType
										user {
											id
											email
											isInternal
										}
									}
								}
							}
							updatedAt
							privateAccess
							state
							favorite
						}
					}
				}
			}
		}
	`,
});
